import { fromZonedTime, toZonedTime } from "date-fns-tz";
var StatusCode = /* @__PURE__ */ ((StatusCode2) => {
  StatusCode2[StatusCode2["BadRequest"] = 400] = "BadRequest";
  StatusCode2[StatusCode2["Conflict"] = 409] = "Conflict";
  StatusCode2[StatusCode2["Forbidden"] = 403] = "Forbidden";
  StatusCode2[StatusCode2["NoContent"] = 204] = "NoContent";
  StatusCode2[StatusCode2["NotFound"] = 404] = "NotFound";
  StatusCode2[StatusCode2["ServerError"] = 500] = "ServerError";
  StatusCode2[StatusCode2["Unauthorized"] = 401] = "Unauthorized";
  return StatusCode2;
})(StatusCode || {});
class FetchPromise extends Promise {
  constructor(executor) {
    super(executor);
    this.abort = () => {
    };
  }
  catch(onrejected) {
    const promise = super.catch(onrejected);
    promise.abort = this.abort;
    return promise;
  }
  finally(onFinally) {
    const promise = super.finally(onFinally);
    promise.abort = this.abort;
    return promise;
  }
  then(onfulfilled, onrejected) {
    const promise = super.then(onfulfilled, onrejected);
    promise.abort = this.abort;
    return promise;
  }
}
class NetworkRequestFailedError extends Error {
  constructor() {
    super("Network request failed");
  }
}
class RequestTimeoutError extends Error {
  constructor() {
    super("Request timeout");
  }
}
class ServiceResponseError extends Error {
  constructor(statusCode, responseText, badRequest, traceId) {
    super();
    this.statusCode = statusCode;
    this.responseText = responseText;
    this.badRequest = badRequest;
    this.traceId = traceId;
    this.retryAuthentication = false;
  }
  get userMessage() {
    var _a;
    return ((_a = this.badRequest) == null ? void 0 : _a.errors) && this.badRequest.errors.length > 0 && this.badRequest.errors[0].errorDescription ? this.badRequest.errors[0].errorDescription : void 0;
  }
}
function createPatch(key, value) {
  return {
    from: null,
    op: "replace",
    path: `/${key}`,
    value
  };
}
const _ServiceBase = class _ServiceBase {
  buildFormData(formParams, getTimeZone) {
    const formData = new FormData();
    const addFormData = (name, value) => {
      if (value === void 0 || value === null) {
        return;
      }
      if (typeof value === "object" && "name" in value) {
        formData.append(name, value, value.name);
      } else if (value instanceof Date) {
        formData.append(
          name,
          this.localZonedDateToISOString(value, getTimeZone, formParams, name)
        );
      } else {
        formData.append(name, value.toString());
      }
    };
    for (const paramName of Object.keys(formParams)) {
      const paramValue = formParams[paramName];
      if (Array.isArray(paramValue)) {
        for (const value of paramValue) {
          addFormData(paramName, value);
        }
      } else {
        addFormData(paramName, paramValue);
      }
    }
    return formData;
  }
  buildJsonData(postData, getTimeZone, parentData, propName) {
    if (postData === void 0 || postData === null) {
      return void 0;
    }
    if (postData instanceof Date) {
      return this.localZonedDateToISOString(
        postData,
        getTimeZone,
        parentData,
        propName
      );
    }
    if (Array.isArray(postData)) {
      for (let i = 0; i < postData.length; i++) {
        postData[i] = this.buildJsonData(
          postData[i],
          getTimeZone,
          postData,
          propName
        );
      }
      return postData;
    }
    if (typeof postData === "object") {
      for (const childPropName of Object.keys(postData)) {
        const data = postData;
        data[childPropName] = this.buildJsonData(
          data[childPropName],
          getTimeZone,
          postData,
          childPropName
        );
      }
    }
    return postData;
  }
  buildUrl(url, pathParams, queryParams, getTimeZone) {
    const urlParts = [this.rootUrl, this.baseUrl];
    if (url) {
      urlParts.push(url);
    }
    let fullUrl = urlParts.map((p) => this.trimSlashes(p)).join("/");
    if (pathParams) {
      for (const paramName of Object.keys(pathParams)) {
        if (pathParams[paramName] !== void 0 && pathParams[paramName] !== null) {
          const paramValue = pathParams[paramName];
          if (Array.isArray(paramValue)) {
            throw new Error("Array not supported in pathParams");
          }
          fullUrl = fullUrl.replace(
            `{${paramName}}`,
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            encodeURIComponent(paramValue.toString())
          );
        }
      }
    }
    if (queryParams) {
      fullUrl += "?";
      const addQueryParam = (name, value) => {
        if (value === void 0 || value === null) {
          return;
        }
        let urlValue = value.toString();
        if (value instanceof Date) {
          urlValue = this.localZonedDateToISOString(
            value,
            getTimeZone,
            queryParams,
            name
          );
        }
        fullUrl += `${name}=${encodeURIComponent(urlValue)}&`;
      };
      for (const paramName of Object.keys(queryParams)) {
        const paramValue = queryParams[paramName];
        if (Array.isArray(paramValue)) {
          for (const value of paramValue) {
            addQueryParam(paramName, value);
          }
        } else {
          addQueryParam(paramName, paramValue);
        }
      }
    }
    return fullUrl;
  }
  requestWithTimeout(params, isRetrying) {
    const controller = new AbortController();
    let timeout = -1;
    let retryTimeout = -1;
    let retryPromise = null;
    const promise = new FetchPromise(async (resolve, reject) => {
      try {
        const retryRequest = () => {
          if (!isRetrying) {
            const retryDelaySeconds = 5;
            retryTimeout = window.setTimeout(() => {
              retryPromise = this.requestWithTimeout(params, true).then(resolve).catch(reject);
            }, retryDelaySeconds * 1e3);
          } else {
            reject(new NetworkRequestFailedError());
          }
        };
        if (!window.navigator.onLine) {
          retryRequest();
          return;
        }
        timeout = window.setTimeout(
          () => {
            reject(new RequestTimeoutError());
            try {
              controller.abort();
            } catch (error) {
              if (typeof window.console !== "undefined") {
                console.log(error);
              }
            }
          },
          (params.timeoutSeconds ?? _ServiceBase.defaultTimeoutSeconds) * 1e3
        );
        let response;
        let resultText;
        let contentType;
        try {
          response = await params.request(controller.signal);
          contentType = response.headers.get("content-type");
          if (!(contentType == null ? void 0 : contentType.includes("application/pdf"))) {
            resultText = await response.text();
          } else {
            resultText = "{}";
          }
        } catch {
          window.clearTimeout(timeout);
          if (controller.signal.aborted) {
            if (typeof window.console !== "undefined") {
              console.log("Request aborted");
            }
          } else if (params.isRetrySafe) {
            retryRequest();
          } else {
            reject(new NetworkRequestFailedError());
          }
          return;
        }
        window.clearTimeout(timeout);
        if (response.status === 400) {
          let badRequest = void 0;
          try {
            const badRequestResponse = JSON.parse(
              resultText
            );
            if ((badRequestResponse == null ? void 0 : badRequestResponse.errors) || (badRequestResponse == null ? void 0 : badRequestResponse.invalidFields) || (badRequestResponse == null ? void 0 : badRequestResponse.missingFields)) {
              badRequest = badRequestResponse;
            }
          } catch (ex) {
            if (typeof window.console !== "undefined") {
              console.error(ex);
            }
          }
          reject(
            new ServiceResponseError(
              400,
              resultText,
              badRequest
            )
          );
          return;
        } else if (response.status === 500) {
          let errorResult = void 0;
          try {
            if (resultText.length > 0) {
              errorResult = JSON.parse(resultText);
            }
          } catch (error) {
            if (typeof window.console !== "undefined") {
              console.error(error);
            }
          }
          reject(
            new ServiceResponseError(
              response.status,
              resultText,
              void 0,
              errorResult == null ? void 0 : errorResult.traceId
            )
          );
          return;
        } else if (!response.ok || response.status < 200 || response.status >= 300 || resultText === "\0") {
          reject(new ServiceResponseError(response.status, resultText));
          return;
        } else if (response.status === 204) {
          resolve();
        }
        let result = void 0;
        if (resultText.length > 0) {
          if (!(contentType == null ? void 0 : contentType.includes("application/pdf"))) {
            result = JSON.parse(resultText);
            result = this.parseDates(
              result,
              void 0,
              "",
              params.getTimeZone
            );
          } else {
            try {
              const blob = await response.blob();
              result = { data: blob };
            } catch {
              reject(new NetworkRequestFailedError());
            }
          }
        }
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    promise.abort = () => {
      try {
        controller.abort();
      } catch (error) {
        if (typeof window.console !== "undefined") {
          console.log(error);
        }
      }
      window.clearTimeout(retryTimeout);
      retryPromise == null ? void 0 : retryPromise.abort();
      if (params.onAbort) {
        params.onAbort();
      }
    };
    return promise;
  }
  localZonedDateToISOString(zonedDate, getTimeZone, data, propName) {
    const timeZone = getTimeZone(
      data,
      propName
    );
    return fromZonedTime(zonedDate, timeZone).toISOString();
  }
  parseDates(data, parentData, propName, getTimeZone) {
    if (data === void 0 || data === null) {
      return data;
    }
    if (Array.isArray(data)) {
      for (let i = 0; i < data.length; i++) {
        data[i] = this.parseDates(data[i], data, propName, getTimeZone);
      }
      return data;
    }
    const dataRecord = data;
    switch (typeof data) {
      case "object":
        for (const childPropName of Object.keys(data)) {
          dataRecord[childPropName] = this.parseDates(
            dataRecord[childPropName],
            data,
            childPropName,
            getTimeZone
          );
        }
        break;
      case "string":
        if (/^\d\d\d\d-\d\d-\d\d/.test(data)) {
          const date = new Date(data);
          if (!isNaN(date.getTime())) {
            return this.utcZonedDateTOBranchDate(
              date,
              getTimeZone,
              parentData,
              propName
            );
          }
        }
        break;
    }
    return data;
  }
  trimSlashes(url) {
    return url.replace(/^\/+|\/+$/g, "");
  }
  utcZonedDateTOBranchDate(utcDate, getTimeZone, data, propName) {
    const timeZone = getTimeZone(
      data,
      propName
    );
    return toZonedTime(utcDate, timeZone);
  }
};
_ServiceBase.defaultTimeoutSeconds = 60;
let ServiceBase = _ServiceBase;
export {
  FetchPromise,
  NetworkRequestFailedError,
  RequestTimeoutError,
  ServiceResponseError,
  StatusCode,
  createPatch,
  ServiceBase as default
};
//# sourceMappingURL=ServiceBase.js.map
